import * as React from "react";
import { appFocusPatientOnDashboard } from "../core/app-bridge.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { usePractice } from "../core/providers/practice.provider.tsx";
import { patientsService } from "../core/services/patients.service.ts";
import {
  appointmentView,
  appointmentsForDate,
  AGENDA_CHAIRS,
  AGENDA_PRACTITIONERS,
  type AgendaAppointmentView,
} from "../agenda/appointments.ts";
import {
  addDays,
  formatHour,
  isSameDay,
  todayLocal,
} from "../agenda/date-utils.ts";
import { DateStepper } from "../shared/selection-nav.component.tsx";
import {
  AdvancedGrid,
  type GridColumn,
} from "../shared/advanced-grid/advanced-grid.component.tsx";

export function PatientsTodayGridWidget({ panelId }: { panelId: string }) {
  const { practice } = usePractice();
  Interactions.usePatient();
  const [selectedDate, setSelectedDate] = React.useState(() => todayLocal());
  const today = todayLocal();
  const rawAppointments = appointmentsForDate(selectedDate, practice.uuid);
  const rows = rawAppointments.map((a) => appointmentView(a));
  const isViewingToday = isSameDay(selectedDate, today);
  const dateLabel = selectedDate.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const columns: GridColumn<AgendaAppointmentView>[] = [
    {
      id: "start",
      label: "Heure",
      accessor: (r) => r.start,
      sortable: true,
      width: "80px",
      render: (r) => (
        <span className="font-semibold tabular-nums">
          {formatHour(r.start)}
        </span>
      ),
    },
    {
      id: "patient",
      label: "Patient",
      accessor: (r) => r.patient,
      sortable: true,
      width: "180px",
      render: (r) => <span className="font-medium">{r.patient}</span>,
    },
    {
      id: "motif",
      label: "Motif",
      accessor: (r) => r.motif,
      sortable: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ background: r.color }}
          />
          {r.motif}
        </span>
      ),
    },
    {
      id: "duration",
      label: "Durée",
      accessor: (r) => r.dur,
      sortable: true,
      width: "80px",
      align: "right",
      render: (r) => (
        <span className="tabular-nums text-sf-text-muted">
          {Math.round(r.dur * 60)} min
        </span>
      ),
    },
    {
      id: "practitioner",
      label: "Praticien",
      accessor: (r) => r.practitioner.name,
      sortable: true,
      width: "130px",
      defaultVisible: false,
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-semibold text-white"
            style={{ background: r.practitioner.color }}
          >
            {r.practitioner.initials}
          </span>
          {r.practitioner.name}
        </span>
      ),
    },
    {
      id: "chair",
      label: "Fauteuil",
      accessor: (r) => r.chair.shortLabel,
      sortable: true,
      width: "90px",
      defaultVisible: false,
      render: (r) => <span className="tabular-nums">{r.chair.shortLabel}</span>,
    },
  ];

  // referenced to avoid unused-import errors when columns hidden by default
  void AGENDA_CHAIRS;
  void AGENDA_PRACTITIONERS;

  return (
    <AdvancedGrid<AgendaAppointmentView>
      instanceId={panelId}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      emptyMessage="Aucun rendez-vous planifié ce jour."
      onRowClick={(r) => {
        const patient = patientsService.findByUuid(
          r.patientUuid,
          practice.uuid,
        );
        if (patient) appFocusPatientOnDashboard(patient);
      }}
      toolbar={
        <div className="flex items-center gap-2">
          <span className="truncate text-[11.5px] capitalize text-sf-text-muted">
            {dateLabel} · {rows.length} RDV
          </span>
          <DateStepper
            onPrev={() => setSelectedDate((d) => addDays(d, -1))}
            onNext={() => setSelectedDate((d) => addDays(d, 1))}
            onToday={() => setSelectedDate(todayLocal())}
            todayActive={isViewingToday}
          />
        </div>
      }
    />
  );
}
